import React, {useState, useCallback, useEffect} from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import DataState from 'app/src/components/DataState';
import Input from 'app/src/elements/inputs';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { View, Text, Pressable } from 'react-native';
import SubNav from './SubNav';
import { useNavigate } from '../../utils/routing';
import { CREATE_ASSET, VIN_TAKEN } from './queries';
import { ButtonContainer } from './elements';
import { useParams } from 'react-router-dom';
import { AssetsContainer } from './elements';
import moment from 'moment';
import Autocomplete from '../Autocomplete';
import AssignToUsers from '../Assets/AssignToUsers';
import CustomLocation from '../Locations/CustomLocation';
import elRed from '../../styles/colors';
import useDebounce from '../../hooks/useDebounce';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewAsset = ({setShowModal, refetch, withNav, sellerIsEquiplinc}) => {
  const { sellerId, sellerName } = useParams();
  const [submitting, setSubmitting] = useState();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedSellerUsers, setSelectedSellerUsers] = useState([]);
  const [showCustomLocationFields, setShowCustomLocationFields] = useState(false);
  const [showDuplicateVin, setShowDuplicateVin] = useState(false);
  const navigate = useNavigate();
  const [createAsset] = useMutation(CREATE_ASSET, {
    onCompleted: () => {
      window.location.href = `/sellers/${sellerId}`;
    }
  });

  const onSubmit = useCallback(input => {
    setSubmitting(true);
    input['year'] = parseInt(input['year']);
    input['miles'] = input['miles'] ? parseInt(input['miles']) : null;
    if(input['acquiredOn']) {
      input['acquiredOn'] = moment(input['acquiredOn']).format('YYYY-MM-DD');
    } else {
      input['acquiredOn'] = null;
    }
    if(input['securedOn']) {
      input['securedOn'] = moment(input['securedOn']).format('YYYY-MM-DD');
    } else {
      input['securedOn'] = null;
    }
    input['customLocation'] = showCustomLocationFields;
    input['userIds'] = selectedUsers;
    input['sellerUserIds'] = selectedSellerUsers;
    if(input['reservePrice'] === "") {
      input['reservePrice'] = null;
    }
    if(input['uccExpiresOn'] === "") {
      input['uccExpiresOn'] = null;
    }
    createAsset({ variables: input });
  });

  const { control, handleSubmit, register, setValue, resetField,
      watch, formState: { errors } } = useForm({
    defaultValues: {
      sellerId: sellerId,
      year: parseInt(moment().format('YYYY')),
      make: '',
      model: '',
      vin: '',
      miles: '',
      reservePrice: '',
      inspectionUrl: '',
      inspectionCompleted: false,
      securedOn: '',
      locationId: '',
      acquired: '',
      acquiredOn: '',
      acquiredPrice: '',
      acquiredExpenses: '',
      feeType: 'Seller',
      feeAmount: '0',
      street: '',
      city: '',
      state: '',
      postalCode: '',
      releaseYardEmail: '',
      releaseYardContact: '',
      showOnWeb: false,
      featured: false,
      newArrival: false,
      drivable: true,
      showListPrice: false,
      listPrice: '',
      uccExpiresOn: '',
      titleApplicableAsset: 'Title'
    }
  });

  const acquired = watch('acquired');
  const watchFeeType = watch('feeType');
  const watchVin = watch('vin');
  const watchDrivable = watch('drivable');
  const watchFeatured = watch('featured');
  const watchShowListPrice = watch('showListPrice');
  const watchInspectionUrl = watch('inspectionUrl');

  const debouncedVin = useDebounce(watchVin);

  const { data: vinData, loading: vinLoading } = useQuery(VIN_TAKEN, {
    variables: { vin: debouncedVin },
    skip: !debouncedVin,
  });

  useEffect(() => {
    if(!debouncedVin || vinLoading) {
      setShowDuplicateVin(false);
      return;
    }

    if(vinData?.vinTaken) {
      if(!window.confirm('An Asset with this VIN already exists within the ' +
        'system. Do you wish to duplicate?'
      )) {
        resetField('vin', '');
      }
      setShowDuplicateVin(true);
    }else {
      setShowDuplicateVin(false);
    }
  }, [debouncedVin, vinData, vinLoading])

  useEffect(() => {
    if (watchInspectionUrl) {
      setValue('inspectionCompleted', true);
    }
  }, [watchInspectionUrl]);

  return (
    <View>
      {withNav &&
        <SubNav sellerId={sellerId} sellerName={sellerName} />
      }
      <AssetsContainer>
        {submitting ?
          DataState.Loading :
          <View>
            <Input.Switch
              label="Launch on Buyer Website?"
              name="showOnWeb"
              control={control}
              errors={errors}
            />
            <Input.Switch
              label="Featured?"
              name="featured"
              control={control}
              errors={errors}
            />
            <View style={{display:'flex', flexDirection: 'row'}}>
              <Input.Switch
                label="New Arrival?"
                name="newArrival"
                control={control}
                errors={errors}
              />
            </View>
            {watchFeatured &&
              <Input.Switch
                label="Show Buy Now Price?"
                name="showListPrice"
                control={control}
                errors={errors}
              />
            }
            {watchFeatured && watchShowListPrice &&
              <Input.Currency
                label="Buy Now Price:"
                name="listPrice"
                control={control}
                errors={errors}
                {...register("listPrice", {
                  valueAsNumber: true,
                  pattern:{
                     value: /^(0|[1-9]\d*)(\.\d+)?$/
                  },
                })}
              />
            }
            <Input.YearPicker
              label="Year:"
              name="year"
              control={control}
              errors={errors}
              {...register("year", {
                required: "Required",
              })}
            />
            {/* <Input.Text
              label="Make:"
              name="make"
              control={control}
              errors={errors}
              {...register("make", {
                required: "Required",
              })}
            /> */}
            <Autocomplete
              label={'Make'}
              placeHolder={''}
              resource={'InputOption'}
              setValue={setValue}
              name='make'
              zIndex={2}
              {...register('make', { required: 'Required' })}
            />
            {/* <Input.Text
              label="Model:"
              name="model"
              control={control}
              errors={errors}
              {...register("model", {
                required: "Required",
              })}
            /> */}
            <Autocomplete
              label={'Model'}
              placeHolder={''}
              resource={'InputOption'}
              setValue={setValue}
              name='model'
              zIndex={1}
              {...register('model', { required: 'Required' })}
            />
            <Input.Text
              label="VIN:"
              name="vin"
              control={control}
              errors={errors}
              {...register('vin', {
                maxLength: { value: 17, message: 'Max 17 characters' }
              })}
            />
            {showDuplicateVin &&
              <View style={{marginTop: '-10px', marginBottom: '10px'}}>
                <Text style={{fontWeight: 'bold', color: elRed}}>
                  Warning: Asset already exists within system!
                </Text>
              </View>
            }
            <Input.Text
              label="Stock Number:"
              name="stockNumber"
              control={control}
              errors={errors}
            />
            <Input.Text
              label="Odometer:"
              name="miles"
              control={control}
              errors={errors}
            />
            <Input.Text
              label="Reserve Price:"
              name="reservePrice"
              control={control}
            />
            <Input.Text
              label="Inspection URL:"
              name="inspectionUrl"
              control={control}
              errors={errors}
              {...register('inspectionUrl', {
                pattern: { value: URL, message: 'Must be a valid URL' }
              })}
            />
            <Input.Switch
              label="Inspection Completed?"
              name="inspectionCompleted"
              control={control}
              errors={errors}
            />
            <Input.Date
              label="Date Secured:"
              name="securedOn"
              control={control}
              errors={errors}
            />
            {sellerIsEquiplinc &&
              <View>
                <Input.Picker
                  label="Acquired?"
                  name="acquired"
                  control={control}
                  errors={errors}
                  options={
                    ["No", "Inherited", "Purchased"].map(option => {
                      return {value: option, label: option}
                    })
                  }
                />
                {['Inherited', 'Purchased'].includes(acquired) && (
                  <View style={{flexDirection: 'row', gap: 10}}>
                    <Input.Date
                      label="Acquired Date:"
                      name="acquiredOn"
                      control={control}
                      errors={errors}
                    />
                    <Input.Text
                      label="Acquired Price:"
                      name="acquiredPrice"
                      control={control}
                    />
                    <Input.Text
                      label="Expenses:"
                      name="acquiredExpenses"
                      control={control}
                    />
                  </View>
                )}
              </View>
            }
            {!showCustomLocationFields &&
              <Autocomplete
                label={'Location'}
                placeHolder={''}
                resource={'Location'}
                setValue={setValue}
                name='locationId'
              />
            }
            <View>
              {!showCustomLocationFields &&
                <Pressable
                  onPress={() => setShowCustomLocationFields(true)}
                >
                  <Text
                    style={{
                      fontSize: '14px',
                      marginTop: '-5px',
                      marginLeft: '10px'
                    }}
                  >
                    Custom Location
                  </Text>
                </Pressable>
              }
            </View>
            {showCustomLocationFields &&
              <CustomLocation
                setShowCustomLocationFields={setShowCustomLocationFields}
                register={register}
                control={control}
                errors={errors}
                setValue={setValue}
              />
            }
            <br />
            <Input.Checkbox
              label="Is Asset Drivable?"
              name="drivable"
              checked={watchDrivable}
              control={control}
              errors={errors}
            />
            <Input.Picker
              label="Sell Fee Type:"
              name="feeType"
              control={control}
              errors={errors}
              options={[
                { value: 'Seller', label: 'Seller Default' },
                { value: 'Percent', label: 'Percent' },
                { value: 'Flat Fee', label: 'Flat Fee' }
              ]}
              {...register('feeType', { required: 'Required' })}
            />
            {'Seller' !== watchFeeType && (
              <Input.Number
                label={
                  `Sell Fee ${'Flat Fee' === watchFeeType ? 'Amount' : '%'}:`
                }
                name="feeAmount"
                control={control}
                errors={errors}
              />
            )}
            <Input.Date
              label="Eligible Sale Date:"
              name="uccExpiresOn"
              control={control}
              errors={errors}
            />
            <Input.Picker
                  label="Which Title Release Process does this Asset Follow?"
                  name="titleApplicableAsset"
                  control={control}
                  errors={errors}
                  options={
                    ["Title", "BOS Only"].map(option => {
                      return {value: option, label: option}
                    })
                  }
                />
            <AssignToUsers
              sellerId={sellerId}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              selectedSellerUsers={selectedSellerUsers}
              setSelectedSellerUsers={setSelectedSellerUsers}
            />
            <ButtonContainer>
              <ButtonGroup>
                <Button label="Create Asset" onPress={handleSubmit(onSubmit)} />
              </ButtonGroup>
            </ButtonContainer>
          </View>
        }
      </AssetsContainer>
    </View>
  )
}

export default NewAsset;
